import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { sessions } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';

export default function Dashboard() {
  const { user } = useAuth();

  const { data: userSessions, isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: sessions.list,
  });

  const stats = {
    totalSessions: userSessions?.length || 0,
    completedSessions: userSessions?.filter(s => s.status === 'completed').length || 0,
    inProgressSessions: userSessions?.filter(s => s.status === 'in_progress').length || 0,
    pausedSessions: userSessions?.filter(s => s.status === 'paused').length || 0,
  };

  const recentSessions = userSessions?.slice(0, 5) || [];

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
    };
    const labels: Record<string, string> = {
      in_progress: '進行中',
      completed: '完了',
      paused: '一時停止',
      cancelled: 'キャンセル',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${styles[status] || styles.cancelled}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* ウェルカムメッセージ */}
        <div className="card jarvis-gradient text-white">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-jarvis-gold flex items-center justify-center">
              <span className="text-jarvis-navy-dark font-bold text-2xl">J</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                おかえりなさいませ、{user?.name}様
              </h1>
              <p className="text-white/80 mt-1">
                本日はどのようなシステムを具現化いたしましょうか？
              </p>
            </div>
          </div>
          <div className="mt-6">
            <Link to="/" className="btn-primary inline-flex items-center gap-2">
              💬 新規ヒアリングを開始
            </Link>
          </div>
        </div>

        {/* 統計カード */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>総セッション</p>
                <p className="text-3xl font-bold mt-1" style={{ color: 'var(--text-primary)' }}>
                  {stats.totalSessions}
                </p>
              </div>
              <div className="text-4xl">📋</div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>完了</p>
                <p className="text-3xl font-bold mt-1 text-green-600">
                  {stats.completedSessions}
                </p>
              </div>
              <div className="text-4xl">✅</div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>進行中</p>
                <p className="text-3xl font-bold mt-1 text-blue-600">
                  {stats.inProgressSessions}
                </p>
              </div>
              <div className="text-4xl">🔄</div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>一時停止</p>
                <p className="text-3xl font-bold mt-1 text-yellow-600">
                  {stats.pausedSessions}
                </p>
              </div>
              <div className="text-4xl">⏸️</div>
            </div>
          </div>
        </div>

        {/* 最近のセッション */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
              最近のセッション
            </h2>
            <Link to="/sessions" className="text-sm text-jarvis-gold hover:underline">
              すべて表示 →
            </Link>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-jarvis-gold border-t-transparent rounded-full" />
            </div>
          ) : recentSessions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-4xl mb-2">💭</p>
              <p style={{ color: 'var(--text-secondary)' }}>
                まだセッションがありません
              </p>
              <Link to="/" className="btn-outline mt-4 inline-block">
                最初のヒアリングを開始
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      プロジェクト
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      進捗
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      ステータス
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      作成日
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                      アクション
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentSessions.map((session) => (
                    <tr
                      key={session.id}
                      className="border-b hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      style={{ borderColor: 'var(--border-color)' }}
                    >
                      <td className="py-3 px-4">
                        <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                          {session.initialIdea?.substring(0, 50) || '（未入力）'}
                          {session.initialIdea && session.initialIdea.length > 50 ? '...' : ''}
                        </p>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-jarvis-gold transition-all"
                              style={{ width: `${(session.answeredQuestions / session.totalQuestions) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {session.answeredQuestions}/{session.totalQuestions}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(session.status)}
                      </td>
                      <td className="py-3 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {new Date(session.createdAt).toLocaleDateString('ja-JP')}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Link
                          to={`/sessions/${session.id}`}
                          className="text-sm text-jarvis-gold hover:underline"
                        >
                          詳細
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

